import React, { useEffect, useState } from "react";
import {
	Container,
	ColumnLayout,
	Box,
	SpaceBetween,
	Checkbox,
	Button,
	DatePicker,
	TimeInput,
	Modal,
	Spinner,
	RadioGroup,
	Multiselect,
	Input,
	Select,
	StatusIndicator,
} from "@amzn/awsui-components-react";
import { useSelector, useDispatch } from 'react-redux'

import { updateFlexicoTemplate } from "src/utilities/FlexicoAPIs";
import { PROCESS_KEYS } from "../Frisco/Constants";
import appSettings from "./app_settings";
import { configureGenericAppSync } from "src/context/AuthContextUtility";
import CustomMultiHybridInputAndSelect from "../Templates/components/CustomMultiHybridInputAndSelect";
import { useSuggestedLogins } from "src/hooks/useSuggestedLogins";
import { getEditableFields } from "../Templates/Utils/FormMetadata";
import { APPROVER_TYPES, FLEXICO_TEMPLATE_KEYS, UPLOADER_TYPES } from "../FlexiCo/Constants";
import { mandatoryFields } from "./FlexicoConstants";
import { alias_dropdown } from './Constants';
import { getQuarterForUpload, validateMandatoryRestrictions } from "./Utility";
import { addDefaultSecAlias } from './AliasUtils';
import { resetTemplateStatus } from "../Templates/Utils/AppActivityUtilsAppsyncPlus";
import { getFriscoGroupsForAppId } from '../Templates/Utils/AppActivityUtils';
import { getDisplayName } from "../utils/displayNameUtils";

const FlexicoConfig = ({
	config,
	addOrreplace,
	setFlexicoConfig,
	getEpoch,
	loadFlexicoDeliverables,
	getDueDates,
	populateDueDateAttributeObject,
	dueDateKeys,
	userId,
}) => {
	const [windowContentLoadingMessage, setWindowContentLoadingMessage] = useState("");
	const [windowContentLoadingError, setWindowContentLoadingError] = useState(null);
	const [configurationWindowContent, setConfigurationWindowContent] = useState(config);
	const [configurationWindowContentOriginal, setConfigurationWindowContentOriginal] = useState(config);
	const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();
	const [fieldMetadata, setFieldMetadata] = useState({});
	const [resetLoadingMessage, setResetLoadingMessage] = useState(null)
	const [dueDatePickerValue, setDueDatePickerValue] = useState("")
	const [selectedWorkstream, setSelectedWorkstream] = useState({ label: "All", value: null });
	const [workstreamOptions, setWorkstreamOptions] = useState([]);

	// Dynamic deliverables state
	const [dynamicDeliverables, setDynamicDeliverables] = useState([]);

	const currentStage = useSelector((globalState) => globalState.auth.currentStage)


	// Functions to manage dynamic deliverables
	const addDeliverable = () => {
		const newId = `deliverable_${Date.now()}`;
		const newDeliverable = {
			id: newId,
			label: '',
			placeholder: 'REF1.1',
			alias_type: newId.toUpperCase()
		};
		setDynamicDeliverables([...dynamicDeliverables, newDeliverable]);
	};

	const removeDeliverable = (id) => {
		if (dynamicDeliverables.length > 1) {
			setDynamicDeliverables(dynamicDeliverables.filter(deliverable => deliverable.id !== id));
			if (configurationWindowContent) {
				const updated = { ...configurationWindowContent };
				delete updated[`${id}_deliverable_enabled`];
				delete updated[`${id}_deliverable`];
				setConfigurationWindowContent(updated);
			}
		}
	};

	const updateDeliverableLabel = (id, newLabel) => {
		setDynamicDeliverables(dynamicDeliverables.map(deliverable =>
			deliverable.id === id ? { ...deliverable, label: newLabel } : deliverable
		));
	};

	useEffect(() => {
		loadEditableFields()
		loadWorkstreams()
		var copyItem = JSON.parse(JSON.stringify(config));
		copyItem = getDueDates(copyItem)
		if (copyItem.custom_attributes) {
			const mandatoryDocsAttribute = copyItem.custom_attributes.find(
				attr => attr.attribute_type === 'MandatorySupportingDocuments'
			);
			if (mandatoryDocsAttribute?.attribute) {
				// Split comma-separated string and reconstruct array of objects
				copyItem.mandatoryDocuments = mandatoryDocsAttribute.attribute.split(',')
					.filter(value => value)
					.map(value => ({
						label: value,
						value: value
					}));
			} else {
				copyItem.mandatoryDocuments = [];
			}
		}

		// Load alias data and update dynamic deliverables
		const updatedDynamicDeliverables = [...dynamicDeliverables];

		if (copyItem.aliases && Array.isArray(copyItem.aliases)) {
			copyItem.aliases.forEach(alias => {
				const existingDeliverable = updatedDynamicDeliverables.find(d => d?.alias_type === alias?.alias_type);
				if (existingDeliverable) {
					copyItem[`${existingDeliverable.id}_deliverable`] = alias?.alias;
					copyItem[`${existingDeliverable.id}_deliverable_enabled`] = true;
				} else {
					const alreadyAdded = updatedDynamicDeliverables.find(d => d?.alias_type === alias?.alias_type);
					if (!alreadyAdded) {
						// Math random id to use for react optimization purpose 
						const newId = `deliverable_${alias?.alias_type.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`;
						// Map the stored ID back to the corresponding label
						const aliasOption = alias_dropdown[currentStage]?.find(option => option.value === alias?.alias_type);
						const labelText = aliasOption ? aliasOption.label : alias?.alias_type;

						const newDeliverable = {
							id: newId,
							label: labelText,
							placeholder: 'REF1.1',
							alias_type: alias?.alias_type
						};
						updatedDynamicDeliverables.push(newDeliverable);
						copyItem[`${newId}_deliverable`] = alias?.alias;
						copyItem[`${newId}_deliverable_enabled`] = true;
					}
				}
			});

		}
		setDynamicDeliverables(updatedDynamicDeliverables);
		setConfigurationWindowContent(copyItem);
		setConfigurationWindowContentOriginal(copyItem)
	}, [config]);

	useEffect(() => {
	}, [configurationWindowContent])

	// Helper function to update custom_attributes
	const updateCustomAttribute = () => {
		try {
			setWindowContentLoadingError(null)
			validateMandatoryRestrictions(configurationWindowContent, dueDateKeys, ['PRE_TAX_DATE'])
		} catch (e) {
			setWindowContentLoadingMessage(null)
			setWindowContentLoadingError(e.message)
			return
		}

		setWindowContentLoadingMessage("Updating the App, please wait");
		let input = {}
		input['flex_id'] = configurationWindowContent['flex_id'].toString()
		input['template_name'] = configurationWindowContent['template_name']
		input[PROCESS_KEYS.app_id] = configurationWindowContent[PROCESS_KEYS.app_id] ? configurationWindowContent[PROCESS_KEYS.app_id].toString() : appSettings[currentStage].appId.toString()
		input[PROCESS_KEYS.approvers] = configurationWindowContent[PROCESS_KEYS.approvers]
		input[PROCESS_KEYS.uploaders] = configurationWindowContent[PROCESS_KEYS.uploaders]
		input['parameters'] = configurationWindowContent['parameters']
		input['label'] = configurationWindowContent['label']
		input['status'] = configurationWindowContent['status']
		input['mapping_id'] = configurationWindowContent['mapping_id']?.toString()
		input['approval_days'] = configurationWindowContent['approval_days']
		input['approval_required'] = configurationWindowContent['approval_required']
		input['description'] = configurationWindowContent['description']
		input['file_types'] = configurationWindowContent['file_types']
		input['group_id'] = configurationWindowContent['group_id'].toString()
		input['next_step'] = configurationWindowContent['next_step']
		input['step_function_name'] = configurationWindowContent['step_function_name']
		if (!input[PROCESS_KEYS.custom_attributes]) {
			input[PROCESS_KEYS.custom_attributes] = []
		}

		for (let dueDateKey in dueDateKeys) {
			populateDueDateAttributeObject(configurationWindowContent, 'FLEXICO', dueDateKey, dueDateKeys[dueDateKey].no_due_attribute_type, dueDateKeys[dueDateKey].ui_keys.no_wb_due_date, dueDateKeys[dueDateKey].ui_keys.due_date, dueDateKeys[dueDateKey].ui_keys.due_time, input)
		}

		// These are for the mandatory files to be uploaded when submit of flexico
		let requiredFields = {}
		requiredFields[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.sub_type] = 'FLEXICO';
		requiredFields[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute_type] = 'MandatorySupportingDocuments';
		requiredFields[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute] = configurationWindowContent?.mandatoryDocuments?.map(option => option.value).join(',') || '';
		if (requiredFields[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute] !== 'NaN') {
			input[PROCESS_KEYS.custom_attributes] = addOrreplace(input[PROCESS_KEYS.custom_attributes], requiredFields)
		}

		let cadence = {}
		cadence[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.sub_type] = 'FLEXICO';
		cadence[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute_type] = 'CADENCE';
		cadence[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute] = configurationWindowContent?.cadence || '';
		if (cadence[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute] !== 'NaN') {
			input[PROCESS_KEYS.custom_attributes] = addOrreplace(input[PROCESS_KEYS.custom_attributes], cadence)
		}

		let disabled = {}
		disabled[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.sub_type] = 'FLEXICO';
		disabled[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute_type] = 'DISABLED';
		disabled[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute] = configurationWindowContent?.disabled || 'false';
		if (disabled[PROCESS_KEYS.CUSTOM_ATTRIBUTES_KEYS.attribute] !== 'NaN') {
			input[PROCESS_KEYS.custom_attributes] = addOrreplace(input[PROCESS_KEYS.custom_attributes], disabled)
		}

		// Handle dynamic deliverable aliases
		if (!input.aliases) {
			input.aliases = []
		}

		// Add aliases for all enabled dynamic deliverables
		dynamicDeliverables.forEach(deliverable => {
			const valueKey = `${deliverable.id}_deliverable`;

			// Use dropdown value directly to get the corresponding ID
			const selectedOption = alias_dropdown[currentStage]?.find(option => option.label === deliverable.label);
			const aliasType = selectedOption ? selectedOption.value : deliverable.label;

			input.aliases.push({
				alias: configurationWindowContent[valueKey], // Reference value from input field
				alias_type: aliasType, // ID from dropdown (22, 24, 25)
				alias_for: "process_name"
			});
		});

		// Add default alias with type "SEC" if not present
		// input.aliases = addDefaultSecAlias(input.aliases, currentStage, input['template_name'], "process_name");

		// Add workstream ID if selected
		if (configurationWindowContent.group_id) {
			input['group_id'] = configurationWindowContent.group_id
		}

		input[PROCESS_KEYS.last_updated_by] = userId

		// setWindowContentLoadingMessage("Updating, please wait..")
		updateFlexicoTemplate(
			input,
			function onSuccess(response) {
				setFlexicoConfig(null);
				loadFlexicoDeliverables();
			},
			function onFailure(error) {
				setFlexicoConfig(null);
			}
		);
	};

	const loadWorkstreams = () => {
		getFriscoGroupsForAppId(appSettings[currentStage].appId, function onSuccess(result) {
			const workstreams = result.data.getFRISCOGroups.map(group => ({
				label: group.group_name,
				value: group.group_id
			}));
			setWorkstreamOptions(workstreams);
		}, function onFailure(result) {
			console.error('Failed to load workstreams:', result);
		});
	}

	const loadEditableFields = async () => {
		let res = await getEditableFields()
		res[PROCESS_KEYS.approvers] = { values: [{ label: "USER", value: APPROVER_TYPES.APPROVER }, { label: "LDAP", value: APPROVER_TYPES.APPROVER_LDAP }] }
		res[PROCESS_KEYS.approvers] = { values: [{ label: "USER", value: APPROVER_TYPES.APPROVER }, { label: "LDAP", value: APPROVER_TYPES.APPROVER_LDAP }] }
		res[PROCESS_KEYS.uploaders] = { values: [{ label: "USER", value: UPLOADER_TYPES.UPLOADER }, { label: "LDAP", value: UPLOADER_TYPES.UPLOADER_LDAP }] }
		setFieldMetadata(res)
	}

	const getValue = (fieldName, subKey = null) => {
		if (subKey) {
			if (configurationWindowContent.hasOwnProperty(fieldName)) {
				return configurationWindowContent?.[fieldName][subKey]
			} else {
				return null
			}
		} else {
			return configurationWindowContent?.[fieldName]
		}

	}

	const postProcessTeamEmails = (value) => {
		return value.replaceAll("@amazon.com", "")
	}


	const approversValidation = (elements) => {
		let emptyValues = true
		elements?.forEach((element) => {
			if (!element.type || !element.alias) {
				emptyValues = false
			}
		})
		return emptyValues
	}

	const stripUniqueItemsAndMerge = (items, newItems, filterCriteria) => {
		let lookup = {}
		let indexes = {}
		let resultItems = []
		newItems.forEach((val) => {
			indexes[val.column_index] = 1
		})
		for (let i = 0; i < items?.length; i++) {
			if (items[i].column_index in indexes) {
				continue
			} else if (items[i].column_index == filterCriteria['column_index']) {
				continue
			}
			else {
				resultItems.push(items[i])
				let hashKey = `${items[i].alias}-${items[i].column_index}-${items[i].type}`
				if (!(hashKey in lookup)) {
					lookup[hashKey] = 1
				}
			}

		}
		for (let i = 0; i < newItems?.length; i++) {
			let hashKey = `${newItems[i].alias}-${newItems[i].column_index}-${newItems[i].type}`
			if (!(hashKey in lookup)) {
				lookup[hashKey] = 1
				resultItems.push(newItems[i])
			}
		}
		return resultItems
	}

	const updateValue = (fieldName, value, subKey = null, filterCriteria = null) => {
		if (subKey) {
			if (!configurationWindowContent.hasOwnProperty(fieldName)) {
				configurationWindowContent[fieldName] = {}
			}
			configurationWindowContent[fieldName][subKey] = value
		} else {
			if (fieldName == PROCESS_KEYS.approvers) {
				configurationWindowContent[fieldName] = stripUniqueItemsAndMerge(configurationWindowContent[fieldName], value, filterCriteria)
			} else {
				configurationWindowContent[fieldName] = value
			}
		}
	}

	const getSelectFieldValues = (fieldName, subKey = null) => {
		if (fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName]['values']) {
			return fieldMetadata[fieldName]['values']
		} else if (subKey && fieldMetadata && fieldMetadata[fieldName] && fieldMetadata[fieldName][subKey] && fieldMetadata[fieldName][subKey]['values']) {
			return fieldMetadata[fieldName][subKey]['values']
		} else {
			return []
		}
	}

	const getQuarter = () => {
		return getQuarterForUpload()
	}

	const resetStatus = (item) => {
		setResetLoadingMessage("Resetting ..")
		var quarterValue = getQuarter()
		resetTemplateStatus(item[FLEXICO_TEMPLATE_KEYS.flex_id], item[FLEXICO_TEMPLATE_KEYS.app_id], quarterValue, ["PreWB", "PostWB", "NoPostWB", "NoPreWB"],
			function onSuccess(result) {
				setResetLoadingMessage(JSON.parse(result?.data.resetTemplateStatus)?.message)
				loadFlexicoDeliverables();
			},
			function onFailure(result) {
				setResetLoadingMessage("Reset failed")
			})
	}



	return (
		<Modal
			onDismiss={() => setFlexicoConfig(null)}
			visible={config}
			header={`Control Panel - ${getDisplayName("", configurationWindowContent[FLEXICO_TEMPLATE_KEYS.template_name], configurationWindowContent[FLEXICO_TEMPLATE_KEYS.label])}`}
			size="large"
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						{windowContentLoadingMessage ? (
							<Box>
								<Spinner></Spinner>
								{windowContentLoadingMessage}
							</Box>
						) : (
							""
						)}
						{
							windowContentLoadingError ?
								<StatusIndicator type="error">{windowContentLoadingError}</StatusIndicator> : ''
						}
						<Button variant="link" onClick={() => setFlexicoConfig(null)}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={updateCustomAttribute}
							disabled={windowContentLoadingMessage}
						>
							Save
						</Button>
					</SpaceBetween>
				</Box>
			}
		>
			<div className='craControlPanel'>
				<Container>
					<ColumnLayout columns={2}>
						<Box>Re-upload input</Box>
						<SpaceBetween direction='horizontal' size="s">
							<Box>
								{getQuarter()}
							</Box>
							<Button variant="primary" onClick={() => { resetStatus(configurationWindowContent) }}>Reset</Button>

							{
								resetLoadingMessage ? <Box float='center'> {resetLoadingMessage}</Box> : ''
							}
						</SpaceBetween>
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Workstream</Box>
						<Select
							selectedOption={workstreamOptions.find(option => option.value === configurationWindowContent?.group_id) || null}
							onChange={({ detail }) => {
								configurationWindowContent['group_id'] = detail.selectedOption?.value;
								setConfigurationWindowContent({ ...configurationWindowContent });
							}}
							options={workstreamOptions}
							placeholder="Select workstream"
						/>
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Tick to disable this deliverable</Box>
						<Checkbox
							onChange={({ detail }) => {
								configurationWindowContent['disabled'] = detail.checked.toString()
								setConfigurationWindowContent({ ...configurationWindowContent })
							}}
							checked={JSON.parse(configurationWindowContent['disabled'] || "false")}
						>
							Disabled
						</Checkbox>
					</ColumnLayout>

					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Pre-WB Due Date</Box>
						<SpaceBetween direction="horizontal" size="s">
							<DatePicker
								onChange={({ detail }) => {
									configurationWindowContent['due_date'] = detail.value
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								value={configurationWindowContent?.due_date || ''}
								openCalendarAriaLabel={(selectedDate) =>
									selectedDate ? `, selected date is ${selectedDate}` : ""
								}
								placeholder="YYYY-MM-DD"

								disabled={JSON.parse(configurationWindowContent?.['no_pre_wb_due_date'] || "false")}
							/>
							<TimeInput
								onChange={({ detail }) => {
									configurationWindowContent['due_time'] = detail.value
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								value={configurationWindowContent?.due_time || ''}
								format="hh:mm"
								placeholder="hh:mm"
								disabled={JSON.parse(configurationWindowContent?.['no_pre_wb_due_date'] || "false")}
							/>

							<Checkbox
								onChange={({ detail }) => {
									configurationWindowContent['no_pre_wb_due_date'] = detail.checked.toString()
									if (detail.checked) {
										configurationWindowContent['due_date'] = ''
										configurationWindowContent['due_time'] = ''
									} else {
										configurationWindowContent['due_date'] = configurationWindowContentOriginal?.['due_date']
										configurationWindowContent['due_time'] = configurationWindowContentOriginal?.['due_time']
									}
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								checked={JSON.parse(configurationWindowContent?.['no_pre_wb_due_date'] || "false")}
							>
								No Pre WB
							</Checkbox>
						</SpaceBetween>
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Post-WB Due Date</Box>
						<SpaceBetween direction="horizontal" size="s">
							<DatePicker
								onChange={({ detail }) => {
									configurationWindowContent['post_wb_due_date'] = detail.value
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								value={configurationWindowContent?.['post_wb_due_date'] || ''}
								openCalendarAriaLabel={(selectedDate) =>
									selectedDate ? `, selected date is ${selectedDate}` : ""
								}
								placeholder="YYYY-MM-DD"
								disabled={JSON.parse(configurationWindowContent?.['no_post_wb_due_date'] || "false")}
							/>
							<TimeInput
								onChange={({ detail }) => {
									configurationWindowContent['post_wb_due_time'] = detail.value
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								value={configurationWindowContent?.['post_wb_due_time'] || ''}
								format="hh:mm"
								placeholder="hh:mm"
								disabled={JSON.parse(configurationWindowContent?.['no_post_wb_due_date'] || "false")}
							/>

							<Checkbox
								onChange={({ detail }) => {
									configurationWindowContent['no_post_wb_due_date'] = detail.checked.toString()
									if (detail.checked) {
										configurationWindowContent['post_wb_due_date'] = ''
										configurationWindowContent['post_wb_due_time'] = ''
									} else {
										configurationWindowContent['post_wb_due_date'] = configurationWindowContentOriginal?.['post_wb_due_date']
										configurationWindowContent['post_wb_due_time'] = configurationWindowContentOriginal?.['post_wb_due_time']
									}
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								checked={JSON.parse(configurationWindowContent?.['no_post_wb_due_date'] || "false")}
							>
								No Post WB
							</Checkbox>
						</SpaceBetween>
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Post-Tax Due Date</Box>
						<SpaceBetween direction='horizontal' size="s">
							<DatePicker
								onChange={({ detail }) => {
									configurationWindowContent['post_tax_date'] = detail.value
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								value={configurationWindowContent?.['post_tax_date'] || ''}
								openCalendarAriaLabel={selectedDate =>
								(selectedDate
									? `, selected date is ${selectedDate}`
									: "")
								}
								placeholder="YYYY-MM-DD"
								disabled={JSON.parse(configurationWindowContent?.['no_post_tax_date'] || "false")}
							/>
							<TimeInput
								onChange={({ detail }) => {
									configurationWindowContent['post_tax_time'] = detail.value
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								value={configurationWindowContent?.['post_tax_time'] || ''}
								format="hh:mm"
								placeholder="hh:mm"
								disabled={JSON.parse(configurationWindowContent?.['no_post_tax_date'] || "false")}
							/>
							<Checkbox
								onChange={({ detail }) => {
									configurationWindowContent['no_post_tax_date'] = detail.checked.toString()
									if (detail.checked) {
										configurationWindowContent['post_tax_date'] = ''
										configurationWindowContent['post_tax_time'] = ''
									} else {
										configurationWindowContent['post_tax_date'] = configurationWindowContentOriginal?.['post_tax_date']
										configurationWindowContent['post_tax_time'] = configurationWindowContentOriginal?.['post_tax_time']
									}
									setConfigurationWindowContent({ ...configurationWindowContent })
								}}
								checked={JSON.parse(configurationWindowContent?.['no_post_tax_date'] || "false")}
							>
								No Post Tax
							</Checkbox>
						</SpaceBetween>
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Cadence</Box>
						<RadioGroup
							onChange={({ detail }) => {
								configurationWindowContent['cadence'] = detail.value
								setConfigurationWindowContent({ ...configurationWindowContent })
							}}
							value={configurationWindowContent?.['cadence']}
							items={[
								{ value: "QUARTERLY", label: "Quarterly" },
								{ value: "ANNUALLY", label: "Annual only" },
								{ value: "BOTH", label: "Both" },
							]}
						/>
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Uploaders</Box>
						<CustomMultiHybridInputAndSelect field={PROCESS_KEYS.uploaders}
							inputKey={PROCESS_KEYS.UPLOADER_KEYS.alias} selectKey={PROCESS_KEYS.UPLOADER_KEYS.type} selectHint="Choose type"
							placeholder="alias" getValues={getValue} postProcess={postProcessTeamEmails} validator={approversValidation}
							updateValueWithFilterCriteria={updateValue} getOptions={getSelectFieldValues} info=' ' errorMessage='type and alias input are mandatory' duplicatesNotAllowed={true}
							loginOptions={suggestedLogins} getLoginOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>Reviewers</Box>
						<CustomMultiHybridInputAndSelect field={PROCESS_KEYS.approvers}
							inputKey={PROCESS_KEYS.APPROVER_KEYS.alias} selectKey={PROCESS_KEYS.APPROVER_KEYS.type} selectHint="Choose type"
							placeholder="alias" getValues={getValue} filterCriteria={{ column_index: 0 }} postProcess={postProcessTeamEmails} validator={approversValidation} indexKey={PROCESS_KEYS.APPROVER_KEYS.column_index} defIndex={0}
							updateValueWithFilterCriteria={updateValue} getOptions={getSelectFieldValues} info=' ' errorMessage='type and alias input are mandatory' duplicatesNotAllowed={true}
							loginOptions={suggestedLogins} getLoginOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
					</ColumnLayout>
					<hr class="rounded"></hr>
					<ColumnLayout columns={2}>
						<Box>L8/Director Reviewers</Box>
						<CustomMultiHybridInputAndSelect field={PROCESS_KEYS.approvers}
							inputKey={PROCESS_KEYS.APPROVER_KEYS.alias} selectKey={PROCESS_KEYS.APPROVER_KEYS.type} selectHint="Choose type"
							placeholder="alias" getValues={getValue} filterCriteria={{ column_index: 1 }} postProcess={postProcessTeamEmails} validator={approversValidation} indexKey={PROCESS_KEYS.APPROVER_KEYS.column_index} defIndex={1}
							updateValueWithFilterCriteria={updateValue} getOptions={getSelectFieldValues} info=' ' errorMessage='type and alias input are mandatory' duplicatesNotAllowed={true}
							loginOptions={suggestedLogins} getLoginOptions={getSuggestedLoginsCaller} aliasesLoading={aliasesLoading} />
					</ColumnLayout>
					<hr class="rounded"></hr>
					<br />
					<ColumnLayout columns={2}>
						<Box>Mandatory Supporting Files</Box>
						<Multiselect
							options={mandatoryFields}
							placeholder="Select the mandatory supporting files to be uploaded"
							checkboxes={true}
							selectedOptions={configurationWindowContent['mandatoryDocuments'] || []}
							onChange={({ detail }) => {
								configurationWindowContent['mandatoryDocuments'] = detail?.selectedOptions
								setConfigurationWindowContent({ ...configurationWindowContent })
							}}
							keepOpen={true}
							filteringType="auto"
						></Multiselect>
					</ColumnLayout>
					<hr class="rounded"></hr>

					<br />

					{/* Dynamic Deliverables Section */}
					<SpaceBetween direction='vertical' size='m'>
						<SpaceBetween direction='horizontal' size='s' alignItems='center'>
							<Box variant="h3">Deliverable References</Box>
							<Button
								iconName="add-plus"
								variant="normal"
								onClick={addDeliverable}
							>
								Add Reference
							</Button>
						</SpaceBetween>

						<ColumnLayout columns={2}>
							<Box variant="awsui-key-label">Team</Box>
							<Box variant="awsui-key-label">Reference</Box>
						</ColumnLayout>

						{dynamicDeliverables.map((deliverable, index) => (
							<div key={deliverable.id}>
								<ColumnLayout columns={2}>
									<Select
										selectedOption={alias_dropdown[currentStage]?.find(option => option.label === deliverable.label) || null}
										onChange={({ detail }) => {
											updateDeliverableLabel(deliverable.id, detail.selectedOption.label);
										}}
										options={alias_dropdown[currentStage]?.filter(option =>
											!dynamicDeliverables.some(d => d.label === option.label && d.id !== deliverable.id)
										) || []}
										placeholder="Select label"
									/>
									<SpaceBetween direction='horizontal' size='l' alignItems='center'>
										<Input
											value={configurationWindowContent[`${deliverable.id}_deliverable`] || ''}
											onChange={({ detail }) => {
												const updated = { ...configurationWindowContent };
												updated[`${deliverable.id}_deliverable`] = detail.value;
												setConfigurationWindowContent(updated);
											}}
											placeholder="REF1.1"
										/>
										<Button
											iconName="remove"
											variant="icon"
											onClick={() => removeDeliverable(deliverable.id)}
										/>
									</SpaceBetween>
								</ColumnLayout>
							</div>
						))}
					</SpaceBetween>
					<br />
				</Container>
			</div>
		</Modal>
	);
};

export default FlexicoConfig;
